<script lang="ts">
  import { setLayerRefs, commit } from './operations/commitOperations';
  import { BookWorkspaceOperators } from './BookWorkspaceOperators';
  import { onDestroy, onMount } from 'svelte';
  import { type LayeredCanvas, Viewport } from '../lib/layeredCanvas/system/layeredCanvas';
  import { PaperRendererLayer } from '../lib/layeredCanvas/layers/paperRendererLayer';
  import { setBubbleCommandTools } from './bubbleinspector/bubbleInspectorStore';
  import { setFrameCommandTools } from './frameinspector/frameInspectorStore';
  import type { Book } from '../lib/book/book';
  import { mainBook, bookOperators, viewport, redrawToken, undoToken, resetFontCacheKey, mainBookExceptionHandler } from './workspaceStore';
  import { buildBookEditor } from './operations/buildBookEditor';
  import { hint } from './bookEditorUtils';
  import AutoSizeCanvas from '../utils/AutoSizeCanvas.svelte';
  import Painter from '../painter/Painter.svelte';
  import type { ArrayLayer } from '../lib/layeredCanvas/layers/arrayLayer';
  import ImageProvider from '../generator/ImageProvider.svelte';
  import { bubbleBucketDirty } from '../bubbleBucket/bubbleBucketStore';
  import { DefaultBubbleSlot } from '../lib/layeredCanvas/layers/bubbleLayer';
  import { currentLocale, type Locale } from '../stores/i18n';
  import { Bubble } from '../lib/layeredCanvas/dataModels/bubble';
  import { fatalStorageError } from '../utils/accountStore';

  let canvas: HTMLCanvasElement;
  let painter: Painter;
  let imageProvider: ImageProvider;

  let layeredCanvas : LayeredCanvas;
  let arrayLayer: ArrayLayer;

  let editingBookId: string | null = null;
  let bookSnapshot: string | null = null;
  let lastBookRef: Book | null = null;

  let operators: BookWorkspaceOperators;
  const defaultBubbleSlot = new DefaultBubbleSlot(new Bubble());

  $: applyDefaultBubbleDirection($currentLocale);
  function applyDefaultBubbleDirection(locale: Locale) {
    defaultBubbleSlot.bubble.direction = locale === 'ja' ? 'v' : 'h';
  }

  $: onUndoCommand($undoToken);
  function onUndoCommand(t: 'undo' | 'redo' | null) {
    $undoToken = null;
    if (t == 'undo') { undo(); }
    if (t == 'redo') { redo(); }
  }

  function undo() {
    if (painter && painter.isPainting()) {
      painter.undo();
    } else if (operators) {
      operators.undo();
    }
  }

  function redo() {
    if (painter && painter.isPainting()) {
      painter.redo();
    } else if (operators) {
      operators.redo();
    }
  }

  $: if ($bubbleBucketDirty) {
    commit(null);
    $bubbleBucketDirty = false;
  }

  $: onChangePaperSize($mainBook?.newPageProperty.paperSize);
  function onChangePaperSize(paperSize: [number, number] | undefined) {
    if (paperSize == null) { return; }
    let modified = false;
    for (const page of $mainBook!.pages) {
      if (page.paperSize[0] === paperSize[0] && page.paperSize[1] === paperSize[1]) {
        continue;
      }
      page.paperSize = [...paperSize];
      modified = true;
    }
    if (!modified) { return; }
    $mainBook = $mainBook;
    commit('page-size');
  }

  $: try {
    onChangeBook(canvas, $mainBook);
    $mainBookExceptionHandler = null;
  }
  catch(e) {
    if ($mainBookExceptionHandler) {
      $mainBookExceptionHandler(e);
    }
  }
  function onChangeBook(canvas: HTMLCanvasElement | null, book: Book | null) {
    if (!canvas || !book) { return; }

    const newBookSnapshot = makeBookSnapshot(book);
    // 参照が同一かつ内容も同じならスキップ。
    // 別インスタンスのBook(同一ファイルの再ロード等)は内容一致でも再構築が必要 - FrameLayerのクロージャが古いpage参照を握ったままになるため。
    if (lastBookRef === book && bookSnapshot === newBookSnapshot) { return; }
    lastBookRef = book;
    bookSnapshot = newBookSnapshot;

    if (!$viewport) {
      console.log("================ viewport remake");
      const v = new Viewport(canvas, (p,s) => hint(canvas, p, s));
      v.translate = [150, 0];
      $viewport = v;
    }
    $viewport.dirty = true;
    editingBookId = book.revision.id;
    
    console.log("*********** buildBookEditor from BookEditor");
    layeredCanvas?.cleanup();

    operators = new BookWorkspaceOperators(canvas, book, () => painter.chase());
    $bookOperators = operators;

    const builtBook = buildBookEditor($viewport, book, $bookOperators, defaultBubbleSlot);
    operators.setBuiltBook(builtBook, defaultBubbleSlot);
    
    // Painterなど他のコンポーネントでも使用するため、ローカル変数に保持
    layeredCanvas = builtBook.layeredCanvas;
    arrayLayer = builtBook.arrayLayer;
    
    setLayerRefs(layeredCanvas, arrayLayer);
    layeredCanvas.redraw();
  }

  function makeBookSnapshot(book: Book) {
    const s = {
      bookId: book.revision.id,
      pages: book.pages.map(p => ({id:p.id, size:p.paperSize, frameTreeId: p.frameTreeId})),
      direction: book.direction,
      wrapMode: book.wrapMode,
      showVideoPlayButton: book.attributes?.showVideoPlayButton ?? true,
      showVideoDottedBorder: book.attributes?.showVideoDottedBorder ?? true,
    };
    return JSON.stringify(s);
  }

  $: setFrameCommandTools(
    painter?.runWithFrame?.bind(painter),
    imageProvider?.run?.bind(imageProvider)
  );

  $: setBubbleCommandTools(
    painter?.runWithBubble?.bind(painter),
    imageProvider?.run?.bind(imageProvider)
  );

  $: onRedraw($redrawToken);
  function onRedraw(token: boolean) {
    if (!token) { return; }
    layeredCanvas?.redraw();
    $redrawToken = false;
  }

  $: onResetFontCache($resetFontCacheKey);
  function onResetFontCache(key: number) {
    if (!arrayLayer) { return; }

    for (const paper of arrayLayer.array.papers) {
      paper.paper?.findLayer(PaperRendererLayer)?.resetCache();
    }
    layeredCanvas.redraw();
  }

  function onResizeCanvas(e: CustomEvent) {
    if (!layeredCanvas || !$viewport) { return; }
    $viewport.dirty = true;
  }

  $: if ($viewport?.dirty) {
    layeredCanvas?.redraw();
  }

  onDestroy(() => {
    layeredCanvas?.cleanup();
  });
</script>

<div class="main-paper-container">
  <AutoSizeCanvas bind:canvas on:resize={onResizeCanvas}>
  </AutoSizeCanvas>

  <!-- fatalErrorStoreでアウトのとき、ここにオーバーレイでエラーが表示される -->
  {#if $fatalStorageError === 'indexeddb-unavailable'}
    <div class="absolute top-0 left-0 w-full h-full bg-white bg-opacity-90 flex flex-col items-center justify-center p-4">
      <h2 class="text-2xl font-bold mb-4">致命的なエラーが発生しました</h2>
      <p class="mb-2">IndexedDBを利用できないため、アプリケーションを正常に動作させることができません。</p>
      <p class="mb-2">以下の対処法をお試しください（要リロード）：</p>
      <ul class="list-disc list-inside mb-4">
        <li>ブラウザの設定でIndexedDBが有効になっていることを確認してください。</li>
        <li>プライベートブラウジングモードを使用している場合は、通常のブラウジングモードで再度お試しください。</li>
      </ul>
      <p>問題が解決しない場合は、<a href="mailto:support@example.com">naoyuki.hirayama@gmail.com</a>までお問い合わせください。</p>
    </div>
  {/if}
</div>

<Painter bind:this={painter} layeredCanvas={layeredCanvas} arrayLayer={arrayLayer} />
<ImageProvider bind:this={imageProvider} />

<style>
  .main-paper-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
